"use client";

import { useState } from "react";
import Link from "@/components/locale-link";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { buildIntroduction } from "@/lib/tool-kit/essay/introduction";

/** English strings. A translated content file overrides these through `ui`. */
export const INTRODUCTION_GENERATOR_STRINGS = {
  hookSentence: "Hook sentence",
  hookPlaceholder:
    "e.g. Every morning, millions of students choose an outfit before they choose a book",
  needAHookTryThe: "Need a hook? Try the ",
  essayHookGenerator: "Essay Hook Generator",
  toBuildOneAroundYourTopic: " to build one around your topic.",
  topicContextLabel: "Topic context — background the reader needs",
  contextPlaceholder:
    "e.g. school dress codes have been debated in the US since the 1990s, with supporters citing discipline and critics citing self-expression",
  thesisStatementLabel: "Thesis statement — your specific claim",
  thesisPlaceholder:
    "e.g. school uniforms benefit students because they reduce social pressure, lower clothing costs, and improve focus",
  buildIntroduction: "Build Introduction",
  copied: "Copied!",
  copy: "Copy",
  fillInAllThreeBoxes: "Fill in all three boxes to build a draft.",
  yourIntroductionDraftLabel:
    "Your introduction draft — edit the transitions so it reads naturally in your voice",
  theFunnelStructure: "The funnel structure: broad to narrow",
  funnelIntro:
    "Strong introductions are shaped like a funnel. They open wide with an attention-grabbing hook, narrow through context that defines the territory, and end at the narrowest point: your thesis. If a sentence in your introduction is broader than the one before it, the funnel is inverted and the paragraph will feel disorganized.",
  funnelStepTitles: [
    "1. Hook — the widest point",
    "2. Context — narrowing down",
    "3. Thesis — the narrowest point",
  ],
  funnelStepBodies: [
    "The opening sentence grabs attention with a question, statistic, quotation, anecdote, or bold statement. It should relate to your topic but does not need to mention your specific argument yet.",
    "One to three sentences of background that define the territory: what the topic is, why it matters, and what the reader needs to know before your claim makes sense. Each sentence should be narrower than the last.",
    "The final sentence states your specific, arguable claim. Everything before it has funneled the reader here, so the body paragraphs can begin proving it immediately.",
  ],
  transitionToUnderstand: "To understand this issue, it helps to know that ",
  transitionBackground:
    "Against this background, the central issue becomes clear.",
  transitionThisEssayArgues: "This essay argues that ",
} as const;

export default function IntroductionGeneratorWidget() {
  const [hook, setHook] = useState("");
  const [context, setContext] = useState("");
  const [thesis, setThesis] = useState("");
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(INTRODUCTION_GENERATOR_STRINGS);
  const locale = useToolLocale();

  const canGenerate =
    hook.trim().length > 0 &&
    context.trim().length > 0 &&
    thesis.trim().length > 0;

  function handleGenerate() {
    if (!canGenerate) {
      return;
    }
    setOutput(
      buildIntroduction(
        { hook, context, thesis },
        {
          toUnderstand: strings.transitionToUnderstand,
          background: strings.transitionBackground,
          thisEssayArgues: strings.transitionThisEssayArgues,
        },
        locale
      )
    );
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const funnelSteps = strings.funnelStepTitles.map((title, index) => ({
    title,
    body: strings.funnelStepBodies[index],
  }));

  return (
    <>
      {/* Hook */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="intro-hook"
        >
          {strings.hookSentence}
        </label>
        <textarea
          className="min-h-[80px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="intro-hook"
          onChange={(e) => setHook(e.target.value)}
          placeholder={strings.hookPlaceholder}
          value={hook}
        />
        <p className="text-muted-foreground text-xs">
          {strings.needAHookTryThe}
          <Link
            className="underline underline-offset-4 hover:text-foreground"
            href="/tools/essay-hook-generator"
          >
            {strings.essayHookGenerator}
          </Link>
          {strings.toBuildOneAroundYourTopic}
        </p>
      </div>

      {/* Context */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="intro-context"
        >
          {strings.topicContextLabel}
        </label>
        <textarea
          className="min-h-[100px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="intro-context"
          onChange={(e) => setContext(e.target.value)}
          placeholder={strings.contextPlaceholder}
          value={context}
        />
      </div>

      {/* Thesis */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="intro-thesis"
        >
          {strings.thesisStatementLabel}
        </label>
        <textarea
          className="min-h-[80px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="intro-thesis"
          onChange={(e) => setThesis(e.target.value)}
          placeholder={strings.thesisPlaceholder}
          value={thesis}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={!canGenerate}
          onClick={handleGenerate}
          type="button"
        >
          {strings.buildIntroduction}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copy}
        </button>
      </div>
      {!canGenerate && (
        <p className="text-muted-foreground text-xs">
          {strings.fillInAllThreeBoxes}
        </p>
      )}

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="intro-output"
          >
            {strings.yourIntroductionDraftLabel}
          </label>
          <textarea
            className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="intro-output"
            onChange={(e) => setOutput(e.target.value)}
            value={output}
          />
        </div>
      )}

      {/* Funnel explainer */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.theFunnelStructure}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.funnelIntro}
        </p>
        <div className="space-y-4">
          {funnelSteps.map((step) => (
            <div
              className="rounded-lg border border-border bg-card p-5"
              key={step.title}
            >
              <h3 className="font-medium text-foreground">{step.title}</h3>
              <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
